import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import {
  CHEQUE_DIRECTIONS,
  CHEQUE_STATUSES,
  type ChequeFilters,
} from '@/features/accounting-cheques/api'
import { ChequesScreen } from '@/features/accounting-cheques/cheques-screen'

const text = (value: unknown) => (typeof value === 'string' && value ? value : undefined)
const positive = (value: unknown) => {
  const number =
    typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN
  return Number.isInteger(number) && number > 0 ? number : undefined
}

/**
 * Route shell for the cheque list (WP09). `cheques.$chequeId.index.tsx` and
 * `cheques.outstanding.tsx` are CHILDREN of this route in flat file routing:
 * without the Outlet below, their URLs render this list instead of themselves.
 * The grant guard is presentation only: the API enforces
 * `accounting_payment.view`.
 */
export const Route = createFileRoute('/_authenticated/accounting/cheques')({
  validateSearch: (search: Record<string, unknown>): ChequeFilters => ({
    direction: CHEQUE_DIRECTIONS.find((direction) => direction === search.direction),
    status: CHEQUE_STATUSES.find((status) => status === search.status),
    q: text(search.q),
    page: positive(search.page),
    perPage: positive(search.perPage),
  }),
  // Cheques are turned off (ADR-0032): this list and its outstanding and
  // detail children send everyone to the bank accounts instead.
  beforeLoad: () => {
    throw redirect({ to: '/accounting/bank-accounts' })
  },
  component: AccountingChequesRoute,
})

function AccountingChequesRoute() {
  const navigate = useNavigate()
  const children = useChildMatches()
  const params = Route.useSearch()

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <ChequesScreen
      params={params}
      onParamsChange={(next) => void navigate({ to: '/accounting/cheques', search: next })}
    />
  )
}
